/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import Welcome from './Welcome'
import apiConfig from './apiConfig'
import { MessageCircleMore, X } from 'lucide-react'
import { trackInteraction } from './utils/trackEvent'
import { themeColors } from './theme'

interface NukeStatus {
  enable_nuke: boolean
  updated_at?: string
  updated_by?: string
  comment?: string
}

const ALLOWED_STATES = [
  'MN',
  'IL',
  'CT',
  'DE',
  'DC',
  'ME',
  'MD',
  'MA',
  'NH',
  'NJ',
  'NY',
  'PA',
  'RI',
  'VT',
  'VA',
  'WV',
  'AL',
  'AR',
  'FL',
  'GA',
  'LA',
  'MS',
  'NC',
  'SC',
  'TN',
  'TX',
  'AZ',
  'CA',
  'NV',
  'NM',
  'OR',
  'WA',
  'AK',
  'HI',
  'IN',
  'IA',
  'KY',
  'MI',
  'MO',
  'MT',
  'ND',
  'OH',
  'WI',
  'ID',
  'OK',
  'SD',
  'KS',
  'NE',
  'WY',
  'UT',
]

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(true)
  const [messages, setMessages] = useState<any[]>([])
  const [enableNuke, setEnableNuke] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isWhitelisted, setIsWhitelisted] = useState(false)
  const [isChatbotOverride, setIsChatbotOverride] = useState(false)
  const [locationChecked, setLocationChecked] = useState(false)

  const [isMobile, setIsMobile] = useState<boolean>(false)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        trackInteraction('Close chat with ESC key', 'keyboard_close')
        setIsOpen(false)
        setShowTooltip(false)
        setMessages([])
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  useEffect(() => {
    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.setAttribute('aria-hidden', isOpen ? 'true' : 'false')
    }
  }, [isOpen])

  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)
  // useEffect(() => {
  //   const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  //   setIsDarkMode(mediaQuery.matches)
  //   const handleChange = (e: MediaQueryListEvent) => setIsDarkMode(e.matches)
  //   mediaQuery.addEventListener('change', handleChange)
  //   return () => mediaQuery.removeEventListener('change', handleChange)
  // }, [])
  const chatEntryRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('chatbot') === 'true') {
      setIsChatbotOverride(true)
      setLocationChecked(true)
      return
    }

    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)
      return match ? match[2] : null
    }

    const extractState = (location: string): string | null => {
      const decodedLocation = decodeURIComponent(location)
      const parts = decodedLocation.split('||')
      if (parts.length < 3 || !parts[2].includes(',')) {
        return null
      }
      const cityStateZip = parts[2].trim().split(' ')
      const extractedState =
        cityStateZip.length > 1 ? cityStateZip[1].replace(',', '') : null
      return extractedState
    }

    const locationCookie = getCookie('userLocation')
    if (locationCookie) {
      const state = extractState(locationCookie)
      if (state && ALLOWED_STATES.includes(state)) {
        setIsWhitelisted(true)
        setEnableNuke(false)
      } else {
        // TODO: Enable it in prod
        setEnableNuke(true)
      }
    } else {
      setEnableNuke(true)
    }

    setLocationChecked(true)
  }, [])

  useEffect(() => {
    if (!locationChecked) return

    if (isChatbotOverride) {
      setLoading(false)
      return
    }

    const fetchNukeStatus = async () => {
      try {
        setLoading(true)
        const response = await fetch(
          `${apiConfig?.chatApi?.host}/candidate_companions/v1/nuke_status`,
          { method: 'GET' },
        )
        if (!response.ok) {
          throw new Error(
            `Failed to fetch nuke status. Code: ${response.status}`,
          )
        }
        const data: NukeStatus = await response.json()
        if (data.enable_nuke) {
          setEnableNuke(true)
        } else if (!enableNuke) {
          setEnableNuke(false)
        } else {
          console.log(
            'Location check enabled nuke, keeping it enabled despite API',
          )
        }
      } catch (err: any) {
        console.error('Error fetching nuke status:', err)
        setError(err.message || 'Error fetching nuke status')
      } finally {
        setLoading(false)
      }
    }

    fetchNukeStatus()
  }, [locationChecked, enableNuke, isChatbotOverride])

  if (!isChatbotOverride && enableNuke) {
    return null
  }

  if (loading && !isChatbotOverride) {
    return null
  }

  const toggleChat = () => {
    if (!isOpen) {
      trackInteraction('Open chat', 'help_button')
      setIsOpen(true)

      setTimeout(() => {
        chatEntryRef.current?.focus()
      }, 50)
    } else {
      trackInteraction('Close chat', 'close_chat_button')
      setIsOpen(false)
    }
    setShowTooltip(false)
  }

  const handleCloseX = () => {
    setIsOpen(false)
    setShowTooltip(false)
    setMessages([])
  }

  const desktopHelpButtonStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '12px 16px',
    width: '95px',
    height: '44px',
    borderRadius: '32px',
    backgroundColor: themeColors.chatbubble,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  }

  const desktopCloseButtonStyle: React.CSSProperties = {
    ...desktopHelpButtonStyle,
    width: '114px',
  }
  const mobileButtonStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    padding: '12px',
    gap: '4px',
    borderRadius: '32px',
    backgroundColor: themeColors.chatbubble,
    color: isDarkMode ? 'black' : 'white',
    border: `2px solid ${isDarkMode ? 'black' : 'white'}`,
    cursor: 'pointer',
  }
  return (
    <div className="chat-widget">
      {isOpen && (
        <div
          className="chat-container"
          role="region"
          aria-modal="true"
          aria-live="polite"
          aria-atomic="true"
          aria-label="Target Careers AI Assistant"
          style={{
            position: 'fixed',
            bottom: isMobile ? '76px' : '76px',
            right: isMobile ? '10px' : '10px',
            left: isMobile ? '10px' : 'auto',
            margin: isMobile ? '0 auto' : undefined,

            zIndex: 1000,
            height: '75vh',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            width: isMobile ? 'calc(100% - 20px)' : undefined,
            maxWidth: '500px',
            background: themeColors.background,
          }}
        >
          <Welcome
            onClose={handleCloseX}
            onMinimize={() => setIsOpen(false)}
            messages={messages}
            setMessages={setMessages}
            chatEntryRef={chatEntryRef}
          />
        </div>
      )}
      {!enableNuke && (
        <button
          id="targetChatButton"
          className="chat-widget-button"
          onClick={toggleChat}
          aria-live="assertive"
          aria-atomic="true"
          aria-label={
            isOpen
              ? 'Close Target Careers AI chat assistant'
              : 'Target Careers AI Assistant'
          }
          style={
            isMobile
              ? mobileButtonStyle
              : isOpen
                ? desktopCloseButtonStyle
                : desktopHelpButtonStyle
          }
        >
          {isMobile ? (
            isOpen ? (
              <X size={24} color={isDarkMode ? 'black' : 'white'} />
            ) : (
              <MessageCircleMore
                size={24}
                color={isDarkMode ? 'black' : 'white'}
              />
            )
          ) : isOpen ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                padding: 0,
                height: '100%',
                width: '82px',
              }}
            >
              Close Chat
            </span>
          ) : (
            <>
              <MessageCircleMore
                size={24}
                color="white"
                style={{
                  padding: 0,
                }}
              />
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  padding: 0,
                  height: '100%',
                  width: '35px',
                }}
              >
                Help
              </span>
            </>
          )}
        </button>
      )}
    </div>
  )
}

export default ChatWidget
